import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"
import tippy from "tippy.js"
import { escapeHtml, safeUrl } from "../lib/search_highlight"

// Typing should stay calm; filters (type / company / archived) should feel instant with a tiny coalesce window.
const RELATIONS_SEARCH_INPUT_DEBOUNCE_MS = 300
const RELATIONS_SEARCH_FILTER_DEBOUNCE_MS = 50

const ADD_RELATION_SPINNER_HTML = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>'

export default class extends Controller {
  static targets = ["searchInput", "typeSelect", "onlyCompanyCheckbox", "includeArchivedCheckbox", "searchResults", "relationsSection", "relationsContent", "companyCheckboxWrapper", "companyName"]
  static values = {
    recordType: String,
    recordId: String,
    companyId: String,
  }

  connect() {
    this.searchTimeout = null
    this.pendingAddKeys = new Set()
    this.relationsData = {}
    this.totalSize = 0

    // Load initial relations data
    this.getRelations()
    // Initial pick list (same debounce path as filter toggles)
    this.queueRelationsSearchFromFilters()

    // Autofocus on the search input when sidepanel opens
    this.searchInputTarget.focus()

    // Update checkbox visibility on initial load
    this.updateCompanyCheckboxVisibility()
    requestAnimationFrame(() => this.refreshCompanyNameTooltip())

    this.setupResizeListener()
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.cleanupResizeListener()

    this.clearCompanyNameTooltip()
  }

  updateSearchType() {
    this.updateCompanyCheckboxVisibility()
    this.queueRelationsSearchFromFilters()
  }

  updateCompanyCheckboxVisibility() {
    if (!this.hasCompanyCheckboxWrapperTarget) return

    const selectedType = this.typeSelectTarget.value

    // Hide the checkbox when Company is selected
    this.companyCheckboxWrapperTarget.classList.toggle('hidden', selectedType === 'Company')
    this.refreshCompanyNameTooltip()
  }

  refreshCompanyNameTooltip() {
    if (!this.hasCompanyNameTarget) return
    const companyNameElement = this.companyNameTarget
    const companyName = companyNameElement.textContent?.trim()

    this.clearCompanyNameTooltip()

    if (!companyName || companyNameElement.scrollWidth <= companyNameElement.clientWidth) return

    companyNameElement.setAttribute("data-tippy-content", companyName)
    tippy(companyNameElement)
  }

  clearCompanyNameTooltip() {
    if (!this.hasCompanyNameTarget) return
    const companyNameElement = this.companyNameTarget

    if (companyNameElement._tippy) {
      companyNameElement._tippy.destroy()
    }

    companyNameElement.removeAttribute("data-tippy-content")
  }

  setupResizeListener() {
    this.resizeHandler = this.debounce(() => {
      this.refreshCompanyNameTooltip()
    }, 250)

    window.addEventListener("resize", this.resizeHandler)
  }

  cleanupResizeListener() {
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
    }
  }

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        func(...args)
      }, wait)
    }
  }

  queueRelationsSearchFromInput() {
    this.scheduleRelationsSearch(RELATIONS_SEARCH_INPUT_DEBOUNCE_MS)
  }

  queueRelationsSearchFromFilters() {
    this.scheduleRelationsSearch(RELATIONS_SEARCH_FILTER_DEBOUNCE_MS)
  }

  scheduleRelationsSearch(delayMs) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      this.searchTimeout = null
      this.runRelationsSearch()
    }, delayMs)
  }

  runRelationsSearch() {
    const query = this.searchInputTarget.value.trim()

    const params = new URLSearchParams({
      q: query,
      is_relations_search: true,
      type: this.typeSelectTarget.value,
      company: this.hasOnlyCompanyCheckboxTarget && this.onlyCompanyCheckboxTarget.checked ? this.companyIdValue : '',
      include_archived: this.hasIncludeArchivedCheckboxTarget && this.includeArchivedCheckboxTarget.checked,
      disable_type: this.recordTypeValue,
      disable_id: this.recordIdValue
    })

    fetch(`/searches?${params}`)
      .then(response => response.json())
      .then(results => {
        this.displaySearchResults(results)
      })
      .catch(error => {
        console.error('Search error:', error)
        this.searchResultsTarget.innerHTML = '<p>Search failed. Please try again.</p>'
      })
  }

  getItemDescription(result) {
    let itemDescription = '';

    if (result.asset_type) {
      switch (result.asset_type) {
        case 'Password':
          itemDescription = `${result.company_name}`;

          if (result.folder) {
            itemDescription += ` / ${result.folder}`;
          }

          break;
        case 'Website':
          itemDescription = `${result.company_name}`;
          break;
        default:
          itemDescription = `${result.asset_type} / ${result.company_name}`;
      }
    }

    if (result.object_type === "Article") {
      itemDescription = result.company_name ? `${result.company_name}` : 'Central KB';

      if (result.folder) {
        itemDescription += ` / ${result.folder}`;
      }
    }

    return itemDescription;
  }

  displaySearchResults(results) {
    if (!results || results.length === 0) {
      this.searchResultsTarget.innerHTML = '<p>No results found.</p>'
      return
    }

    const html = results.map(result => `
      <div class="result-item">
        <div class="item-content">
          <a href="${safeUrl(result.url)}" target="_blank" class="item-title">
            ${escapeHtml(result.name)}
          </a>
          <p class="item-description">${escapeHtml(this.getItemDescription(result))}</p>
        </div>
        <button type="button"
                class="button button--icon button--icon--small button--secondary button--unfilled"
                aria-label="Add relation"
                data-action="click->relations-sidepanel#addRelation"
                data-toable-id="${escapeHtml(result.id)}"
                data-toable-type="${escapeHtml(result.object_type)}">
          <i class="fas fa-plus" aria-hidden="true"></i>
        </button>
      </div>
    `).join('')

    this.searchResultsTarget.innerHTML = html
    this.applyPendingAddButtonStates()
  }

  pendingKeyForAdd(toableId, toableType) {
    return `${toableType}:${toableId}`
  }

  applyPendingAddButtonStates() {
    if (!this.hasSearchResultsTarget || this.pendingAddKeys.size === 0) {
      return
    }

    this.searchResultsTarget
      .querySelectorAll('button[data-action*="addRelation"]')
      .forEach((btn) => {
        const key = this.pendingKeyForAdd(btn.dataset.toableId, btn.dataset.toableType)
        if (this.pendingAddKeys.has(key)) {
          btn.disabled = true
          btn.setAttribute("aria-busy", "true")
          btn.innerHTML = ADD_RELATION_SPINNER_HTML
        }
      })
  }

  restoreAddRelationButton(button, originalButtonHTML) {
    if (!button.isConnected) {
      return
    }
    button.disabled = false
    button.removeAttribute("aria-busy")
    button.innerHTML = originalButtonHTML
  }

  addRelation(event) {
    const button = event.currentTarget
    const toableId = button.dataset.toableId
    const toableType = button.dataset.toableType
    const pendingKey = this.pendingKeyForAdd(toableId, toableType)
    const originalButtonHTML = button.innerHTML

    this.pendingAddKeys.add(pendingKey)
    button.disabled = true
    button.setAttribute("aria-busy", "true")
    button.innerHTML = ADD_RELATION_SPINNER_HTML

    fetch('/relations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        relation: {
          fromable_type: this.recordTypeValue,
          fromable_id: this.recordIdValue,
          toable_type: toableType,
          toable_id: toableId
        }
      })
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        return response.json().then(errorData => {
          throw new Error(errorData.error || 'Failed to add relation')
        })
      }
    })
    .then(data => {
      this.getRelations()
      this.runRelationsSearch()
      this.notifyRelationsChanged()
      flash("Relation Added!", "success")
    })
    .catch(error => {
      console.error('Error adding relation:', error)
      flash(error.message || "Something went wrong!", "error")
      this.restoreAddRelationButton(button, originalButtonHTML)
    })
    .finally(() => {
      this.pendingAddKeys.delete(pendingKey)
    })
  }

  removeRelation(event) {
    const relationId = event.currentTarget.dataset.relationId

    if (confirm('Are you sure you want to delete this relation?')) {
      fetch(`/relations/${relationId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      .then(response => {
        if (response.ok) {
          // Refresh the relations data from server to get proper grouping
          this.getRelations()
          // Refresh search results to reflect the change
          this.runRelationsSearch()
          // Notify other components that relations have changed
          this.notifyRelationsChanged()
          flash("Relation removed!", "success")
        } else {
          throw new Error('Failed to remove relation')
        }
      })
      .catch(error => {
        console.error('Error removing relation:', error)
        flash("Something went wrong!", "error")
      })
    }
  }

  getRelations() {
    // Use the same endpoint as the Vue component, with from_sidebar param to include passwords
    fetch(`/relations/get_relations?id=${this.recordIdValue}&type=${this.recordTypeValue}&from_sidebar=true`)
      .then(response => response.json())
      .then(data => {
        // Update our state
        this.relationsData = data.relations
        this.totalSize = data.total_size
        this.renderRelations()
      })
      .catch(error => {
        console.error('Error getting relations:', error)
        flash("Something went wrong!", "error")
      })
  }

  renderRelations() {
    if (!this.hasRelationsContentTarget) return

    let html = ''

    const currentTotal = Object.values(this.relationsData).reduce((sum, group) => {
      const items = group.items ? group.items.length : 0
      const inherited = group.inherited_items ? group.inherited_items.length : 0
      return sum + items + inherited
    }, 0)

    if (currentTotal === 0) {
      html += `
        <div class="results-group">
          <p id ="empty-relations-message">None</p>
        </div>
      `
    } else {
      Object.keys(this.relationsData).forEach(key => {
        const relationGroup = this.relationsData[key]
        const items = relationGroup.items || []
        const inheritedItems = relationGroup.inherited_items || []

        if (items.length > 0 || inheritedItems.length > 0) {
          const groupCount = items.length + inheritedItems.length
          html += `
            <div class="results-group">
              <div class="group-header">
                <i class="${escapeHtml(relationGroup.icon)}"></i>
                ${escapeHtml(relationGroup.name)} (${groupCount})
              </div>
          `

          if (inheritedItems.length > 0) {
            html += `<div class="relation-inherited-label">Inherited from process</div>`
            inheritedItems.forEach(relation => {
              html += this.renderRelationItem(relation)
            })
          }

          if (items.length > 0) {
            if (inheritedItems.length > 0) {
              html += `<hr class="relation-section-divider">`
            }
            items.forEach(relation => {
              html += this.renderRelationItem(relation, relationGroup)
            })
          }

          html += '</div>'
        }
      })
    }

    this.relationsContentTarget.innerHTML = html
  }

  renderRelationItem(relation, relationGroup) {
    let url
    if (relation.toable_url) {
      url = relation.toable_url
    } else if (relation.url) {
      url = relation.url
    } else if (relation.toable_id && relation.toable_type) {
      url = this.generateUrlForRelation(relation)
    } else {
      url = '#'
      console.warn('Unable to determine URL for relation:', relation)
    }

    const canDelete = relationGroup && !relationGroup.linked

    return `
      <div class="result-item">
        <div class="item-content">
          <a href="${safeUrl(url)}" target="_blank" class="item-title">
            ${escapeHtml(relation.name)}
          </a>
          ${relation.description ? `<p class="item-description">${escapeHtml(relation.description)}</p>` : ''}
        </div>
        ${canDelete ? `
          <button class="delete-button"
                  data-action="click->relations-sidepanel#removeRelation"
                  data-relation-id="${escapeHtml(relation.id)}">
            <i class="fas fa-trash"></i>
          </button>
        ` : ''}
      </div>
    `
  }

  notifyRelationsChanged() {
    // Dispatch a custom event that the Vue component can listen for
    const event = new CustomEvent('relations:updated', {
      detail: {
        recordType: this.recordTypeValue,
        recordId: this.recordIdValue
      }
    })
    window.dispatchEvent(event)
  }

  generateUrlForRelation(relation) {
    // Generate URL based on relation type
    const type = relation.toable_type.toLowerCase()
    const id = relation.toable_id

    switch(type) {
      case 'asset':
        return `/a/${id}`
      case 'article':
        return `/kba/${id}`
      case 'assetpassword':
        return `/passwords/${id}`
      case 'website':
        return `/websites/${id}`
      case 'network':
        return `/networks/${id}`
      case 'vlan':
        return `/vlans/${id}`
      default:
        return `/${type}s/${id}`
    }
  }
}
